/**
 * Find the first dom node that matches the given `predicate` by walking up the
 * DOM. This method can return the `start` node if it matches.
 *
 * @param start The node to start the search from.
 * @param predicate A predicate function receiving the current DOM node.
 * @param stopParent The node to stop the search at. If this is reached before a
 *     node is found, `null` is returned. Defaults to the `document`.
 *
 * @return The first matching node, or `null` if none is found before
 *     `stopParent` is reached.
 */
export function getFirstMatchingParent(
  start: HTMLElement,
  predicate: (ele: HTMLElement) => boolean,
  stopParent: HTMLElement = (document as unknown) as HTMLElement
): HTMLElement | null {
  for (
    let el = start;
    el !== stopParent && el;
    el = el.parentNode as HTMLElement
  ) {
    if (predicate(el)) {
      return el;
    }
  }

  return null;
}

export function createElement(
  tagName: string,
  attributes: object = {},
  children: HTMLElement[] = []
) {
  const ele = document.createElement(tagName);

  for (const a in attributes) {
    if (a === '__text') {
      ele.innerText = attributes[a];
    } else {
      ele.setAttribute(a, attributes[a]);
    }
  }

  children.forEach(c => ele.appendChild(c));

  return ele;
}

export function prependChild(parent: HTMLElement, child: HTMLElement) {
  if (parent.firstChild) {
    parent.insertBefore(child, parent.firstChild);
  } else {
    parent.appendChild(child);
  }
}

/**
 * Create an array of items by evaluating the given `cb` function for
 * every number from [0, `steps`).
 * @param steps The number of items to create.
 * @param cb Called for every step.
 * @return An array of results from calling `cb`.
 */
export function rangeMap<T>(steps: number, cb: (step: number) => T): T[] {
  const items = [];
  for (let i = 0; i < steps; ++i) {
    items.push(cb(i));
  }
  return items;
}

/**
 * Standalone function equivalent of calling `Object.hasOwnProperty`.
 * @param obj The object to check for a property on.
 * @param prop The property to check for.
 * @return Whether `obj` has its own `prop`.
 */
export function has(obj: object, prop: string) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}
